import { BreathingPhase } from '@/lib/hooks/use-exercises';

interface BreathingOverlayProps {
  phases: BreathingPhase[];
  bpm: number;
  introDelay: number;
  totalRounds: number;
  audioTime: number;
  audioPlaying: boolean;
}

export function BreathingOverlay({
  phases,
  bpm,
  introDelay,
  totalRounds,
  audioTime,
  audioPlaying,
}: BreathingOverlayProps) {
  const beatDuration = 60 / bpm;
  const phaseDurations = phases.map((p) => p.duration * beatDuration);
  const roundDuration = phaseDurations.reduce((a, b) => a + b, 0);
  const totalDuration = roundDuration * totalRounds;

  const inIntro = audioTime < introDelay;
  const elapsed = Math.max(0, audioTime - introDelay);
  const finished = elapsed >= totalDuration;

  let currentRound = Math.min(Math.floor(elapsed / roundDuration), totalRounds - 1);
  let timeInRound = elapsed - currentRound * roundDuration;

  if (finished) {
    currentRound = totalRounds - 1;
    timeInRound = roundDuration;
  }

  let phaseIdx = 0;
  let phaseElapsed = timeInRound;
  for (let i = 0; i < phaseDurations.length; i++) {
    if (phaseElapsed < phaseDurations[i]) {
      phaseIdx = i;
      break;
    }
    phaseElapsed -= phaseDurations[i];
    if (i === phaseDurations.length - 1) phaseIdx = i;
  }

  const currentPhase = phases[phaseIdx];
  const phaseDur = phaseDurations[phaseIdx];
  const phaseProgress = phaseDur > 0 ? Math.min(phaseElapsed / phaseDur, 1) : 0;

  let circleScale = 0;
  if (inIntro || finished) {
    circleScale = 0;
  } else if (currentPhase.action === 'inhale') {
    circleScale = phaseProgress;
  } else if (currentPhase.action === 'exhale') {
    circleScale = 1 - phaseProgress;
  } else if (currentPhase.action === 'hold') {
    const prevIdx = phaseIdx > 0 ? phaseIdx - 1 : phases.length - 1;
    circleScale = phases[prevIdx].action === 'inhale' ? 1 : 0;
  }

  const circleSize = 120 + circleScale * 120;

  const beatsElapsed = Math.floor(phaseElapsed / beatDuration) + 1;
  const totalBeats = Math.round(phaseDur / beatDuration);
  const beatCount = Math.min(beatsElapsed, totalBeats);

  let label = '';
  if (finished) label = 'Klaar!';
  else if (inIntro) label = 'Luister naar de introductie...';
  else label = currentPhase.label;

  return (
    <div className="flex flex-col items-center py-4">
      <div className="relative flex items-center justify-center" style={{ height: 240, width: 240 }}>
        <div
          className="absolute rounded-full transition-all duration-[1400ms] ease-in-out"
          style={{
            width: circleSize + 20,
            height: circleSize + 20,
            background: 'radial-gradient(circle, rgba(74,222,128,0.15) 0%, transparent 70%)',
          }}
        />
        <div
          className="rounded-full flex items-center justify-center transition-all duration-[1400ms] ease-in-out border-2 border-green-400/50"
          style={{
            width: circleSize,
            height: circleSize,
            background: `radial-gradient(circle, rgba(74,222,128,${0.15 + circleScale * 0.2}) 0%, rgba(74,222,128,${0.05 + circleScale * 0.1}) 100%)`,
            boxShadow: `0 0 ${30 + circleScale * 30}px rgba(74,222,128,${0.2 + circleScale * 0.2})`,
          }}
        >
          <div className="text-center">
            <p className="text-white text-lg font-semibold">{label}</p>
            {!inIntro && !finished && (
              <p className="text-white/80 text-3xl font-bold mt-1">{beatCount}</p>
            )}
          </div>
        </div>
      </div>

      {!inIntro && (
        <div className="mt-4 flex items-center gap-2">
          {Array.from({ length: totalRounds }).map((_, i) => (
            <div
              key={i}
              className={`h-2 w-2 rounded-full transition-colors ${
                i <= currentRound && !inIntro ? 'bg-green-400' : 'bg-white/30'
              }`}
            />
          ))}
          <span className="text-white/60 text-sm ml-2">
            Ronde {Math.min(currentRound + 1, totalRounds)}/{totalRounds}
          </span>
        </div>
      )}
    </div>
  );
}
